// Conversations accordion data — fetches the per-project threads index lazily
// the first time the accordion expands. Same load-once + manual `refetch`
// shape as `useProjectIssues`.

import { useCallback, useEffect, useRef, useState } from "react";

import {
  ProjectThreadsResponseSchema,
  type ProjectThread,
} from "@loom/shared/dashboard";

import { httpGet, UnauthorizedError } from "../lib/http";

export interface ProjectThreadsState {
  readonly threads: ReadonlyArray<ProjectThread>;
  readonly loading: boolean;
  readonly error: string | null;
  readonly refetch: () => void;
}

export const useProjectThreads = (
  projectId: number,
  enabled: boolean,
): ProjectThreadsState => {
  const [threads, setThreads] = useState<ReadonlyArray<ProjectThread>>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [tick, setTick] = useState<number>(0);
  const aborted = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    aborted.current = false;
    setLoading(true);
    setError(null);
    httpGet(
      `/api/projects/${projectId}/threads`,
      ProjectThreadsResponseSchema,
    )
      .then((value) => {
        if (aborted.current) return;
        setThreads(value.threads);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (aborted.current) return;
        if (err instanceof UnauthorizedError) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => {
      aborted.current = true;
    };
  }, [projectId, enabled, tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return { threads, loading, error, refetch };
};
